import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Building2, Clock, ArrowRight, Dumbbell, Scissors, Stethoscope, Palette, GraduationCap, Briefcase } from 'lucide-react';

const businessTypes = [
  { id: 'gimnasio', label: 'Gimnasio', icon: Dumbbell },
  { id: 'spa', label: 'Spa', icon: Palette },
  { id: 'clinica', label: 'Clínica', icon: Stethoscope },
  { id: 'barberia', label: 'Barbería', icon: Scissors },
  { id: 'academia', label: 'Academia', icon: GraduationCap },
  { id: 'consultorio', label: 'Consultorio', icon: Briefcase },
];

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [schedule, setSchedule] = useState('09:00 - 21:00');
  const [loading, setLoading] = useState(false);
  const { completeOnboarding } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!businessName.trim()) {
      toast.error('Ingresa el nombre de tu negocio');
      return;
    }
    if (!businessType) {
      toast.error('Selecciona el tipo de negocio');
      return;
    }
    
    setLoading(true);
    try {
      await completeOnboarding({ 
        business_name: businessName, 
        business_type: businessType, 
        schedule 
      });
      toast.success('¡Configuración completada!');
      navigate('/dashboard');
    } catch (err) {
      toast.error('Error al guardar configuración');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl"
      >
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-12">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                s <= step ? 'w-12 bg-white' : 'w-6 bg-white/20'
              }`}
            />
          ))}
        </div>

        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {step === 1 && 'Configura tu negocio'}
            {step === 2 && '¿Qué tipo de negocio tienes?'}
            {step === 3 && 'Horario de atención'}
          </h1>
          <p className="text-white/50">
            {step === 1 && 'Cuéntanos un poco sobre tu negocio'}
            {step === 2 && 'Esto nos ayuda a personalizar tu experiencia'}
            {step === 3 && 'Define el horario de operación'}
          </p>
        </div>

        {/* Step 1: Business Name */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="relative">
              <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                data-testid="onboarding-business-name"
                className="w-full pl-12 pr-4 py-4 bg-[#0a0a0a] border border-white/[0.1] rounded-xl text-white text-lg placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="Nombre de tu negocio"
                autoFocus
              />
            </div>
            <button
              onClick={() => businessName.trim() && setStep(2)}
              disabled={!businessName.trim()}
              data-testid="onboarding-next-1"
              className="w-full py-4 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continuar
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {/* Step 2: Business Type */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {businessTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setBusinessType(type.id)}
                  data-testid={`onboarding-type-${type.id}`}
                  className={`
                    p-6 rounded-xl border transition-all text-left
                    ${businessType === type.id 
                      ? 'bg-white text-black border-white' 
                      : 'bg-[#0a0a0a] text-white border-white/[0.08] hover:border-white/20'
                    }
                  `}
                >
                  <type.icon className={`w-8 h-8 mb-3 ${businessType === type.id ? 'text-black' : 'text-white/60'}`} />
                  <span className="font-medium">{type.label}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-4 bg-white/[0.05] text-white font-semibold rounded-xl border border-white/[0.1] hover:bg-white/[0.08] transition-all"
              >
                Atrás
              </button>
              <button
                onClick={() => businessType && setStep(3)}
                disabled={!businessType}
                data-testid="onboarding-next-2"
                className="flex-1 py-4 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                Continuar
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Schedule */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="relative">
              <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                value={schedule}
                onChange={(e) => setSchedule(e.target.value)}
                data-testid="onboarding-schedule"
                className="w-full pl-12 pr-4 py-4 bg-[#0a0a0a] border border-white/[0.1] rounded-xl text-white text-lg placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="Ej: 09:00 - 21:00"
              />
            </div>
            <p className="text-sm text-white/40 text-center">
              Podrás modificar esto después en configuración
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setStep(2)}
                className="flex-1 py-4 bg-white/[0.05] text-white font-semibold rounded-xl border border-white/[0.1] hover:bg-white/[0.08] transition-all"
              >
                Atrás
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                data-testid="onboarding-complete"
                className="flex-1 py-4 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    Comenzar
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
